import React from 'react';
import { FaRobot, FaUserCircle } from 'react-icons/fa';

// Função para processar texto e detectar links, números de telefone, etc.
const processText = (text) => {
  if (!text) return text;
  
  // Detectar URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  // Detectar números de telefone (formato brasileiro)
  const phoneRegex = /(\(?\d{2}\)?\s?\d{4,5}-?\d{4})/g;
  // Detectar emails
  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  
  let match;
  
  // Encontrar todas as ocorrências
  const matches = [];
  
  // URLs
  while ((match = urlRegex.exec(text)) !== null) {
    matches.push({ type: 'url', ...match });
  }
  
  // Telefones
  while ((match = phoneRegex.exec(text)) !== null) {
    matches.push({ type: 'phone', ...match });
  }
  
  // Emails
  while ((match = emailRegex.exec(text)) !== null) {
    matches.push({ type: 'email', ...match });
  }
  
  // Ordenar por posição
  matches.sort((a, b) => a.index - b.index);
  
  // Construir elementos
  let currentIndex = 0;
  const elements = [];
  
  matches.forEach((match) => {
    // Adicionar texto antes do match
    if (match.index > currentIndex) {
      elements.push({ type: 'text', content: text.substring(currentIndex, match.index) });
    }
    
    // Adicionar o match
    if (match.type === 'url') {
      elements.push({ 
        type: 'link', 
        content: match[0], 
        href: match[0],
        target: '_blank',
        rel: 'noopener noreferrer'
      });
    } else if (match.type === 'phone') {
      const phoneNumber = match[0].replace(/\D/g, '');
      elements.push({ 
        type: 'link', 
        content: match[0], 
        href: `tel:${phoneNumber}`,
        target: '_self'
      });
    } else if (match.type === 'email') {
      elements.push({ 
        type: 'link', 
        content: match[0], 
        href: `mailto:${match[0]}`,
        target: '_self'
      });
    }
    
    currentIndex = match.index + match[0].length;
  });
  
  // Adicionar texto restante
  if (currentIndex < text.length) {
    elements.push({ type: 'text', content: text.substring(currentIndex) });
  }
  
  // Se não houver matches, retornar texto simples
  if (elements.length === 0) {
    return [{ type: 'text', content: text }];
  }
  
  return elements;
};

const MessageBubble = React.memo(({ msg, isThinking }) => {
  const processedText = React.useMemo(() => processText(msg.text), [msg.text]);
  
  return (
    <div
      key={msg.id}
      className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"} mb-2`}
    >
      <div
        className={`group max-w-[80%] rounded-2xl px-3 py-2 shadow-sm transition-colors duration-300 ${
          msg.sender === "user"
            ? "bg-blue-500 text-white rounded-br-sm"
            : "bg-white text-gray-800 border border-gray-200 rounded-bl-sm"
        } ${msg.sender === "bot" && isThinking ? "animate-pulse" : ""}`}
      >
        <div className="flex items-start gap-2">
          {msg.sender !== "user" ? (
            <div className="mt-0.5 shrink-0 bg-gradient-to-br from-blue-500 to-blue-600 p-1.5 rounded-lg shadow-sm">
              <FaRobot className="text-white" size={16} />
            </div>
          ) : (
            <div className="mt-0.5 shrink-0 bg-blue-500 p-1.5 rounded-full">
              <FaUserCircle className="text-white" size={16} />
            </div>
          )}
          <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">
            {processedText.map((element, idx) => {
              if (element.type === 'link') {
                return (
                  <a
                    key={idx}
                    href={element.href}
                    target={element.target}
                    rel={element.rel}
                    className={`underline hover:opacity-80 transition-opacity ${
                      msg.sender === "user" 
                        ? "text-blue-100 hover:text-white" 
                        : "text-blue-600 hover:text-blue-800"
                    }`}
                  >
                    {element.content}
                  </a>
                );
              }
              return <span key={idx}>{element.content}</span>;
            })}
            {msg.sending && <span className="ml-2 text-xs opacity-90">⏳</span>}
          </div>
        </div>
      </div>
    </div>
  );
});

MessageBubble.displayName = 'MessageBubble';

export default MessageBubble;